import { Country } from "@func-core/core/domain/view-models";
import { CountryCode } from "@settings/countries";

import { LOCALES } from "./locales";

export type LandingFooterConfig = {
  socialNetworkLinks?: {
    type: string;
    url: string;
  }[];
  resourceLinks?: {
    url: string;
    text: string;
  }[];
  companyLinks?: {
    url: string;
    text: string;
  }[];
};

export interface LandingCountryConfig {
  benefits: boolean;
  reimbursement: boolean;
  showCompanyValuesSection: boolean;
  showCommittedToAnimalsSection: boolean;
  showAssociationsSection: boolean;
  appReimbursement: boolean;
  PreSalesFaqs: boolean;
  faqs: boolean;
  reviewsWidget?: {
    title: string;
    widgetId: string;
  };
  landingBottomCta: boolean;
  kspList: {
    description?: string;
    title: string;
    items?: string;
    image: string;
  }[];
  showChatLink?: boolean;
  footerConfig: LandingFooterConfig;
}

export const COUNTRIES: Record<string, Country> = {
  ES: {
    name: "Spain",
    language: LOCALES.es,
    prefixPhoneNumber: "34",
    currency: "EUR",
    iso: "ES",
  },
  DE: {
    name: "Germany",
    language: LOCALES.de,
    prefixPhoneNumber: "49",
    currency: "EUR",
    iso: "DE",
  },
  FR: {
    name: "France",
    language: LOCALES.fr,
    prefixPhoneNumber: "33",
    currency: "EUR",
    iso: "FR",
  },
  IT: {
    name: "Italy",
    language: LOCALES.it,
    prefixPhoneNumber: "39",
    currency: "EUR",
    iso: "IT",
  },
  PT: {
    name: "Portugal",
    language: LOCALES.pt,
    prefixPhoneNumber: "351",
    currency: "EUR",
    iso: "PT",
  },
  FI: {
    name: "Finland",
    language: LOCALES.fi,
    prefixPhoneNumber: "358",
    currency: "EUR",
    iso: "FI",
  },
  GR: {
    name: "Greece",
    language: LOCALES.el,
    prefixPhoneNumber: "30",
    currency: "EUR",
    iso: "GR",
  },
  NL: {
    name: "Netherlands",
    language: LOCALES.nl_NL,
    prefixPhoneNumber: "31",
    currency: "EUR",
    iso: "NL",
  },
  PL: {
    name: "Poland",
    language: LOCALES.pl_PL,
    prefixPhoneNumber: "48",
    currency: "PLN",
    iso: "PL",
  },
  AT: {
    name: "Austria",
    language: LOCALES.de_AT,
    prefixPhoneNumber: "43",
    currency: "EUR",
    iso: "AT",
  },
  NO: {
    name: "Norway",
    language: LOCALES.nb_NO,
    prefixPhoneNumber: "47",
    currency: "NOK",
    iso: "NO",
  },
  IE: {
    name: "Ireland",
    language: LOCALES.en_IE,
    prefixPhoneNumber: "353",
    currency: "EUR",
    iso: "IE",
  },
  HU: {
    name: "Hungary",
    language: LOCALES.hu_HU,
    prefixPhoneNumber: "36",
    currency: "HUF",
    iso: "HU",
  },
  RO: {
    name: "Romania",
    language: LOCALES.ro_RO,
    prefixPhoneNumber: "40",
    currency: "RON",
    iso: "RO",
  },
  CZ: {
    name: "Czech Republic",
    language: LOCALES.cs_CZ,
    prefixPhoneNumber: "420",
    currency: "CZK",
    iso: "CZ",
  },
};

const commonKspList = [
  {
    description: "landing.ksp_list.item_1.description",
    title: "landing.ksp_list.item_1.title",
    image: "world",
  },
  {
    description: "landing.ksp_list.item_2.description",
    title: "landing.ksp_list.item_2.title",
    image: "dogAndCat",
  },
  {
    description: "landing.ksp_list.item_3.description",
    title: "landing.ksp_list.item_3.title",
    image: "syringe",
  },
];

export const LANDING_CONFIG: Record<CountryCode, LandingCountryConfig> = {
  ES: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: true,
    showCommittedToAnimalsSection: true,
    showAssociationsSection: true,
    appReimbursement: true,
    PreSalesFaqs: false,
    faqs: true,
    reviewsWidget: {
      title: "landing.reviews.title",
      widgetId: "cb5fc09ef406f62c944190ae62668a4790319b64",
    },
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [
        {
          type: "facebook",
          url: "https://www.facebook.com/barkibu",
        },
        {
          type: "twitter",
          url: "https://twitter.com/barkibu",
        },
        {
          type: "instagram",
          url: "https://www.instagram.com/barkibu",
        },
      ],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/es",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/es/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "https://www.barkibu.com/blog/programa-afiliados",
          text: "landing.footer.afiliate-program",
        },
        {
          url: "/es/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/sobre-nosotros/",
          text: "landing.footer.about-us",
        },
        {
          url: "/politica-de-privacidad/",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/cookies/",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/terminos-y-condiciones/",
          text: "landing.footer.terms-conditions",
        },
        {
          url: "https://www.barkibu.com/blog",
          text: "landing.footer.blog",
        },
        {
          url: "/seguros-perros/",
          text: "landing.footer.dog-insurance",
        },
        {
          url: "/seguros-gatos/",
          text: "landing.footer.cat-insurance",
        },
        {
          url: "/seguros-cachorros/",
          text: "landing.footer.puppy-insurance",
        },
      ],
    },
  },
  PT: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: false,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: true,
    PreSalesFaqs: false,
    faqs: true,
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [
        {
          type: "instagram",
          url: "https://www.instagram.com/barkibu.pt",
        },
      ],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/pt-pt",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/pt/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "mailto:assurances@barkibu.com",
          text: "landing.footer.contact",
        },
        {
          url: "/pt/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/politica-de-privacidade/",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/politica-de-utilizacao-de-cookies/",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/termos-e-condicoes/",
          text: "landing.footer.terms-conditions",
        },
      ],
    },
  },
  FR: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: false,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: true,
    PreSalesFaqs: false,
    faqs: true,
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [
        {
          type: "facebook",
          url: "https://www.facebook.com/barkibu",
        },
        {
          type: "twitter",
          url: "https://twitter.com/barkibu",
        },
        {
          type: "instagram",
          url: "https://www.instagram.com/barkibu",
        },
      ],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/fr",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://barkibu1.typeform.com/to/RZcu4tvP",
          text: "landing.footer.contract-end",
        },
        {
          url: "mailto:assurances@barkibu.com",
          text: "landing.footer.contact",
        },
        {
          url: "/fr/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/a-propos-de-nous/",
          text: "landing.footer.about-us",
        },
        {
          url: "/politique-de-confidentialite",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/politique-cookies",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/termes-et-conditions",
          text: "landing.footer.terms-conditions",
        },
        {
          url: "https://help.barkibu.com/hc/fr/articles/24754735129874-Qui-sont-nos-partenaires",
          text: "landing.footer.our-partners",
        },
      ],
    },
  },
  DE: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: true,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: true,
    PreSalesFaqs: false,
    faqs: true,
    reviewsWidget: {
      title: "landing.reviews.title",
      widgetId: "13c23dacfc7f2ef0197d1f6fb372538645f7aa1c",
    },
    landingBottomCta: true,
    kspList: [
      {
        title: "landing.ksp_list.item_1.title",
        items: "landing.ksp_list.item_1.list",
        image: "umbrella",
      },
      {
        title: "landing.ksp_list.item_2.title",
        items: "landing.ksp_list.item_2.list",
        image: "dogAndCat",
      },
      {
        title: "landing.ksp_list.item_3.title",
        items: "landing.ksp_list.item_3.list",
        image: "syringe",
      },
    ],
    footerConfig: {
      socialNetworkLinks: [],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/de",
          text: "landing.footer.faqs",
        },
        {
          url: "/allgemeine-geschaeftsbedingungen.pdf",
          text: "landing.footer.sample-contract",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/de/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "/de/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/uber-uns/",
          text: "landing.footer.about-us",
        },
        {
          url: "/daten",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/cookie-richtlinie",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/nutzungsbedingungen",
          text: "landing.footer.terms-conditions",
        },
        {
          url: "/erstinformation",
          text: "landing.footer.initial-information",
        },
        {
          url: "/impressum",
          text: "landing.footer.print",
        },
      ],
    },
  },
  IT: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: false,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: true,
    PreSalesFaqs: false,
    faqs: true,
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [
        {
          type: "instagram",
          url: "https://www.instagram.com/barkibu.it",
        },
      ],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/it",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/it/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "mailto:assurances@barkibu.com",
          text: "landing.footer.contact",
        },
        {
          url: "/condizioni-generali.pdf",
          text: "landing.footer.information-details",
        },
        {
          url: "/it/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/informativa-sulla-privacy/",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/informativa-sui-cookie/",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/termini-e-condizioni/",
          text: "landing.footer.terms-conditions",
        },
      ],
    },
  },
  FI: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: false,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: false,
    PreSalesFaqs: true,
    faqs: true,
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/fi-fi",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/fi/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "/fi/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/tietosuojakaytantomme/",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/barkibun-evastekaytanto/",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/kayttoehdot/",
          text: "landing.footer.terms-conditions",
        },
      ],
    },
  },
  GR: {
    benefits: true,
    reimbursement: true,
    showCompanyValuesSection: false,
    showCommittedToAnimalsSection: false,
    showAssociationsSection: false,
    appReimbursement: false,
    PreSalesFaqs: true,
    faqs: true,
    landingBottomCta: true,
    kspList: commonKspList,
    footerConfig: {
      socialNetworkLinks: [],
      resourceLinks: [
        {
          url: "https://help.barkibu.com/hc/el-gr",
          text: "landing.footer.faqs",
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.barkibu.app",
          text: "landing.footer.android-download",
        },
        {
          url: "https://apps.apple.com/gr/app/barkibu-salud-de-mascotas/id1514417760",
          text: "landing.footer.ios-download",
        },
        {
          url: "/el/apple-pay",
          text: "landing.footer.apple-pay",
        },
      ],
      companyLinks: [
        {
          url: "https://medium.com/@aldamiz/we-are-hiring-b3e13dd1a7fb",
          text: "landing.footer.hiring",
        },
        {
          url: "/politiki-aporritou/",
          text: "landing.footer.privacy-policy",
        },
        {
          url: "/politiki-cookies-tis-barkibu/",
          text: "landing.footer.cookie-policy",
        },
        {
          url: "/oroi-kai-proypotheseis-chrisis/",
          text: "landing.footer.terms-conditions",
        },
      ],
    },
  },
};

export const AVAILABLE_COUNTRIES = {
  ES: COUNTRIES.ES,
  DE: COUNTRIES.DE,
  FR: COUNTRIES.FR,
  IT: COUNTRIES.IT,
  PT: COUNTRIES.PT,
  FI: COUNTRIES.FI,
  GR: COUNTRIES.GR,
};
export const PRESALES_COUNTRIES = {};
export const DEFAULT_COUNTRY = AVAILABLE_COUNTRIES.ES;
